import { duplicateFoodRules } from "../../backend/src/rules/duplicate-food.js";
import type { ParsedVoiceMealDraft, NutritionRowEstimate } from "./remote-meal-parser";

export class MealCommandCancelled extends Error { constructor() { super("meal_command_cancelled"); } }
export interface DuplicateFoodQuestion { name: string; portion: string; matches: Array<{ index: number; name: string; portion: string }> }
export type DuplicateFoodChoice = "add" | number | null;
type Ask = (question: DuplicateFoodQuestion) => Promise<DuplicateFoodChoice>;
let promptHandler: Ask | null = null;
export function registerDuplicateFoodPrompt(handler: Ask) {
  promptHandler = handler;
  return () => { if (promptHandler === handler) promptHandler = null; };
}
export function askDuplicateFood(question: DuplicateFoodQuestion) {
  if (!promptHandler) throw new Error("duplicate_prompt_unavailable");
  return promptHandler(question);
}
const normalize = (name: string) => name.toLocaleLowerCase("ru-RU").replace(/ё/g, "е").replace(/[^\p{L}\p{N}]+/gu, " ").trim().split(/\s+/u).sort().join(" ");
export function foodNameSimilarity(first: string, second: string): number {
  const a = normalize(first), b = normalize(second);
  if (!a || !b) return 0;
  let row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const next = [i];
    for (let j = 1; j <= b.length; j++) next[j] = Math.min(next[j - 1]! + 1, row[j]! + 1, row[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    row = next;
  }
  return 1 - row[b.length]! / Math.max(a.length, b.length);
}
function totals(draft: ParsedVoiceMealDraft, rows: NutritionRowEstimate[]): ParsedVoiceMealDraft {
  const sum = (key: "calories" | "protein" | "fat" | "carbs") => Math.round(rows.reduce((n, row) => n + (row[key] ?? 0), 0) * 10) / 10;
  return { ...draft, nutrition: { rows, totalCalories: sum("calories"), totalProtein: sum("protein"), totalFat: sum("fat"), totalCarbs: sum("carbs") } };
}

// Work on a private draft. The caller receives a result only after every choice.
export async function resolveDuplicateAdditions(original: ParsedVoiceMealDraft, proposed: ParsedVoiceMealDraft, answer: string, ask: Ask, edit: (draft: ParsedVoiceMealDraft, index: number, answer: string) => Promise<ParsedVoiceMealDraft>): Promise<ParsedVoiceMealDraft> {
  if (duplicateFoodRules.explicitExtra.test(answer) && !duplicateFoodRules.negatedExtra.test(answer)) return proposed;
  const count = original.items.length;
  if (!count || proposed.items.length <= count || !original.items.every((item, i) => JSON.stringify(item) === JSON.stringify(proposed.items[i]))) return proposed;
  if (!original.nutrition || !proposed.nutrition) throw new Error("missing_nutrition");
  let result = original;
  for (let i = count; i < proposed.items.length; i++) {
    const item = proposed.items[i]!;
    const row: NutritionRowEstimate = proposed.nutrition.rows[i]!;
    const matches = result.items.flatMap((old, index) => foodNameSimilarity(old.name, item.name) >= duplicateFoodRules.threshold ? [{ index, name: old.name, portion: result.nutrition!.rows[index]!.portion }] : []);
    const choice = matches.length ? await ask({ name: item.name, portion: row.portion, matches }) : "add";
    if (choice === null) throw new MealCommandCancelled();
    if (choice === "add") {
      const index = result.items.length;
      result = totals({ ...result, items: [...result.items, item], clarifications: [...result.clarifications, ...proposed.clarifications.filter(q => q.itemIndex === i).map(q => ({ ...q, itemIndex: index }))] }, [...result.nutrition!.rows, row]);
    } else {
      if (!matches.some(match => match.index === choice)) throw new Error("invalid_duplicate_choice");
      const changed = await edit(result, choice, `Измени выбранную позицию: ${item.sourceText || item.name}`);
      if (changed.items.length !== result.items.length || !changed.nutrition || !changed.nutrition.rows[choice]) throw new Error("unsafe_duplicate_edit");
      result = totals({ ...result, items: result.items.map((old, index) => index === choice ? changed.items[index]! : old), clarifications: [...result.clarifications.filter(q => q.itemIndex !== choice), ...changed.clarifications.filter(q => q.itemIndex === choice)] }, result.nutrition!.rows.map((old, index) => index === choice ? changed.nutrition!.rows[index]! : old));
    }
  }
  return { ...result, sourceText: proposed.sourceText, ...(proposed.foodLookupCompleted !== undefined ? { foodLookupCompleted: proposed.foodLookupCompleted } : {}), ...(proposed.ingredientNoticeId ? { ingredientNoticeId: proposed.ingredientNoticeId } : {}) };
}
